<template>
  <div v-loading="loading">
    <el-form ref="creativeForm" :model="creativeModel" :rules="rules" label-width="110px" label-position="left">
      <ContentBox title="展示配置">
        <el-row>
          <el-col :span="18">
            <el-form-item label="展示方式">
              <el-radio-group v-model="creativeModel.exposureMode">
                <el-radio-button
                  v-for="(item, index) in exposureModeOptions"
                  :label="item.label"
                  :key="index"
                >{{ item.value }}</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </ContentBox>
      <ContentBox title="素材配置">
        <el-tabs
          v-model="activeTab"
          class="media-tabs"
          type="card"
          closable
          :addable="creativeModel.mediaModelList.length < adSpaceModel.maxFrequency"
          @edit="handleTabsEdit"
        >
          <el-tab-pane
            v-for="(item, tabIndex) in creativeModel.mediaModelList"
            :key="tabIndex"
            :label="'素材' + [tabIndex + 1]"
            :name="tabIndex + ''"
          >
            <MaterialOwnSourceItem
              :adSpaceModel="adSpaceModel"
              :mediaModelList="creativeModel.mediaModelList"
              :mediaIndex="tabIndex"
              :mediaModel="item"
              :shareTrackModel="shareTrackModel"
              :scheduleForm="scheduleForm"
              :providerList="providerList"
              :recStreamInfo="recStreamInfo"
              :sdkProviderList="sdkProviderList"
              :pointList="pointList"
            />
          </el-tab-pane>
        </el-tabs>
      </ContentBox>
      <ContentBox v-if="showSettlement" title="结算监测">
        <el-row>
          <el-col :span="18">
            <el-form-item label="结算监测类型" prop="settlementType">
              <el-radio-group v-model="creativeModel.settlementType" :disabled="settlementDisabled" @change="changeSettlementType">
                <el-radio-button
                  v-for="(item, index) in settlementTypeOptions"
                  :label="item.label"
                  :key="index"
                >{{ item.value }}</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="creativeModel.settlementType !== 0">
          <el-col :span="18">
            <el-form-item label="结算监测商" prop="settlementProvider">
              <el-select v-model="creativeModel.settlementProvider" :disabled="settlementDisabled">
                <el-option
                  v-for="(item, index) in settlementProviderOptions"
                  :key="index"
                  :label="item.providerName"
                  :value="item.providerNo"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </ContentBox>
      <ContentBox class="content-box">
        <el-button plain @click="handleGoBack">返回</el-button>
        <el-button type="primary" :loading="loading" @click="saveCreative">提交送审</el-button>
      </ContentBox>
    </el-form>
  </div>
</template>

<script>
import { exposureModeOptions, settlementTypeOptions, genTargetEvent, genTracks, eventEnum, genMediaModel } from './MaterialCreative'
import { setTargetEventDetail, checkTargetEvent, checkTrackCode, shareTrackCode, checkRegEx, filterDefaultTips, checkTipsDetail } from './creativeUtil'
import MaterialOwnSourceItem from './MaterialOwnSourceItem.vue'
import { uniq } from 'lodash'
import { computed } from 'vue'

export default {
  components: {
    MaterialOwnSourceItem,
  },
  props: {
    scheduleForm: {
      type: Object,
      default: () => ({})
    },
    sdkProviderList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeTab: 0,
      adSpaceModel: {
        maxFrequency: 1
      },
      creativeModel: this.genCreativeModel(),
      shareTrackModel: {
        tabTrackType: 'trackList',
        ...genTracks()
      },
      macroMap: {},
      providerMap: new Map(),
      providerList: [],
      spaceTipsTempId: undefined,
      spaceTipsDetail: '',
      loading: false,
      settlementDisabled: false,
      rules: {
        settlementProvider: [{ required: true, message: '请选择结算监测商', trigger: 'blur' }]
      },
      recStreamInfo: null,
      exposureModeOptions,
      settlementTypeOptions,
      ownProvider: {},
      pointList: [],
    }
  },
  provide() {
    return {
      adSpaceModel: computed(() => this.adSpaceModel),
      scheduleForm: computed(() => this.scheduleForm),
      spaceTipsTempId: computed(() => this.spaceTipsTempId),
      spaceTipsDetail: computed(() => this.spaceTipsDetail),
      providerList: computed(() => this.providerList),
      sdkProviderList: computed(() => this.sdkProviderList),
    }
  },
  inject: ['eventTempList'],
  computed: {
    showSettlement() {
      return this.creativeModel.mediaModelList.some(item => item.codeType !== 0)
    },
    settlementProviderOptions() {
      if (this.creativeModel.settlementType === 0) {
        return []
      }
      const settlementTrackType = settlementTypeOptions.find(item => item.label === this.creativeModel.settlementType)
      const fieldName = settlementTrackType.key
      let providerNos = []
      if (this.creativeModel.mediaModelList[0].codeType !== 1) {
        const firstMediaProviderNos = uniq(this.creativeModel.mediaModelList[0][fieldName].map(item => item.serviceProvider))
        providerNos = firstMediaProviderNos.filter(item => {
          return this.creativeModel.mediaModelList.every(media => {
            const curMediaProviderNos = uniq(media[fieldName].map(item => item.serviceProvider))
            return curMediaProviderNos.includes(item)
          })
        })
      } else {
        providerNos = uniq(this.shareTrackModel[fieldName].map(item => item.serviceProvider))
      }
      return this.providerList.filter(item => providerNos.includes(item.providerNo))
    }
  },
  async created() {
    this.loading = true
    this.getCodeMacroMap()
    this.getTrackProviderList()
    this.getOwnTrackProvider()
    await this.getSpaceModel()
    if (this.adSpaceModel.pointTrack === 1) {
      this.getPointList()
    }
    // 品牌商城广告获取推荐流尺寸
    if (this.scheduleForm.spaceNo === 'CCADTV10036' && this.scheduleForm.recId) {
      this.getRecStreamInfo()
    }
    await this.getSpaceDefaultTips()
    this.getCreativeDetail()
  },
  watch: {
    settlementProviderOptions: {
      handler(newVal) {
        if (this.creativeModel.settlementProvider) {
          const provider = newVal.find(item => item.providerNo === this.creativeModel.settlementProvider)
          !provider && (this.creativeModel.settlementProvider = '')
        }
      },
      deep: true
    }
  },
  methods: {
    genCreativeModel() {
      return {
        partnerId: this.scheduleForm.partnerId,
        scheduleId: this.scheduleForm.scheduleId,
        exposureMode: 1,
        mediaModelList: [genMediaModel()],
        settlementType: 0,
        settlementProvider: '',
        shareTrack: 0
      }
    },
    getCreativeDetail() {
      this.$service.scheduleMediaDetails(this.scheduleForm.scheduleId).then(res => {
        this.loading = false
        this.creativeModel.exposureMode = res.exposureMode
        this.creativeModel.shareTrack = res.shareTrack
        this.creativeModel.settlementType = res.settlementType
        this.creativeModel.settlementProvider = res.settlementProvider
        this.settlementDisabled = [4, 9].includes(res.scheduleStatus) && res.settlementType !== 0
        if (res.mediaVOList.length === 0) {
          // 计划单还未添加素材，设置默认项
          this.creativeModel.mediaModelList[0].tipsTempId = this.spaceTipsTempId
          this.creativeModel.mediaModelList[0].tipsDetail = filterDefaultTips(this.spaceTipsDetail)
          this.creativeModel.mediaModelList[0].additionalMediaList = []
        } else {
          // 计划单已添加素材，回显
          this.creativeModel.mediaModelList = res.mediaVOList.map(media => {
            const codeType = media.shareTrack === 1 ? 1 : (this.existTrackCode(media, '') ? 2 : 0)
            const codeTypeLanding = this.existTrackCode(media, 'Landing') ? 1 : 0
            const retMedia = {
              ...media,
              landPage: media.landingPage ? 1 : 0,
              locationType: media.mediaDetailList.length ? media.mediaDetailList[0].locationType : 0,
              ...setTargetEventDetail(media, this.eventTempList),
              codeType,
              oringinalCodeType: codeType,
              codeTypeLanding,
              tabTrackType: 'trackList',
              tabTrackTypeLanding: 'trackList',
              codeTypeDisabled: codeType === 2 && [4, 6].includes(res.scheduleStatus),
              creativeTitle: media.feedContent ? media.feedContent.creativeTitle : '',
              clickButtonContent: media.feedContent ? media.feedContent.clickButtonContent : '',
              pointTrackList: (media.pointTrackList || []).map((item) => {
                item.point = `${item.point}`
                item.trackDisabled = item.serviceProvider === this.ownProvider.provider
                return item
              }),
              additionalMediaList: (media.additionalMediaList || []).map(item => {
                return {
                  ...item,
                  ...setTargetEventDetail(item, this.eventTempList),
                  partnerName: media.partnerName,
                  adSpaceName: media.adSpaceName,
                }
              })
            }
            if (media.mobileTrackList && media.mobileTrackList.length > 0) {
              retMedia.useMobileTrack = 1
            } else {
              retMedia.useMobileTrack = 0
              retMedia.mobileTrackList = [{ trackTypeModel: 1, serviceProvider: '', code: '' }]
            }
            retMedia.landingTipsDetail = (media.landingTipsDetail && media.landingTipsDetail[0])
              ? media.landingTipsDetail.map(tips => tips.replace(/\\"/g, '"'))
              : ['']
            return retMedia
          })
          if (res.shareTrack === 1) {
            const mediaModel = {
              ...res.mediaVOList[0],
              pointTrackList: res.mediaVOList[0].pointTrackList || [],
            }
            shareTrackCode(this.shareTrackModel, mediaModel)
          }
        }
      })
        .catch(() => {
          this.loading = false
        })
    },
    existTrackCode(media, type = '') {
      let result = media[`trackList${type}`].length ||
        media[`clickTrackList${type}`].length ||
        media[`startTrackList${type}`].length ||
        media[`middleTrackList${type}`].length ||
        media[`endTrackList${type}`].length
      if (type === '') {
        result = result || media[`pointTrackList${type}`].length
      }
      return result
    },
    getRecStreamInfo() {
      this.$service.getRecStreamInfo({ recId: this.scheduleForm.recId }).then(res => {
        this.recStreamInfo = res || null
      })
    },
    getCodeMacroMap() {
      this.$service.getCodeMacroMap().then((data) => {
        this.macroMap = data.codeMacroMap
      })
    },
    getOwnTrackProvider() {
      this.$service
        .getCoocaaTrack({ trackType: 'track' })
        .then((res) => {
          if (res) {
            this.ownProvider = res || {}
          }
        })
    },
    getTrackProviderList() {
      this.$service.getAvailProviderList({ scheduleId: this.scheduleForm.scheduleId }).then((res) => {
        this.providerList = res || []
        if (this.providerList) {
          let providerMap = new Map()
          this.providerList.forEach((item) => {
            providerMap.set(item.providerNo, item.checkCode)
          })
          this.providerMap = providerMap
        }
      })
    },
    getSpaceModel() {
      const params = { spaceId: this.scheduleForm.spaceId }
      return this.$service.getSpaceModelById(params).then(res => {
        this.$set(this, 'adSpaceModel', res)
      })
    },
    getPointList() {
      this.$service.getPointList().then((res) => {
        this.pointList = res
      })
    },
    getSpaceDefaultTips() {
      return this.$service
        .getAdDefaultTip({ spaceId: this.scheduleForm.spaceId, defFlagType: 0 })
        .then((res) => {
          if (res) {
            this.spaceTipsTempId = res.tempId
            this.spaceTipsDetail = res.tipsDetail ? JSON.stringify(res.tipsDetail) : ''
          }
        })
    },
    handleTabsEdit(name, action) {
      const index = parseInt(name)
      if (action === 'add') {
        const mediaModel = genMediaModel()
        mediaModel.tipsTempId = this.spaceTipsTempId
        mediaModel.tipsDetail = filterDefaultTips(this.spaceTipsDetail)
        // 监测代码素材通用
        const firstMediaModel = this.creativeModel.mediaModelList[0]
        if (firstMediaModel.codeType === 1) {
          mediaModel.codeType = 1
          mediaModel.oringinalCodeType = 1
        }
        this.creativeModel.mediaModelList.push(mediaModel)
        this.activeTab = `${this.creativeModel.mediaModelList.length - 1}`
      }
      if (action === 'remove') {
        const mediaModelList = this.creativeModel.mediaModelList
        if (mediaModelList.length === 1) {
          return
        }
        if (mediaModelList[index].mediaId) {
          this.$confirm('此操作将从创意中清除该素材, 是否继续?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            center: true,
            showClose: false
          })
            .then(() => {
              this.creativeModel.mediaModelList.splice(index, 1)
              this.activeTab = `${this.creativeModel.mediaModelList.length - 1}`
            })
            .catch(() => {})
        } else {
          this.creativeModel.mediaModelList.splice(index, 1)
          this.activeTab = `${this.creativeModel.mediaModelList.length - 1}`
        }
      }
    },
    changeSettlementType() {
      this.creativeModel.settlementProvider = ''
    },
    async saveCreative() {
      if (!await this.validateCreative()) return
      const params = this.buildApiParam()
      this.loading = true
      this.$service
        .saveMaterial(params, '操作成功')
        .then(() => {
          this.loading = false
          this.$emit('go-back')
        })
        .catch(() => {
          this.loading = false
        })
    },
    buildApiParam() {
      const mediaAddUpdateVOList = this.creativeModel.mediaModelList.map(item => {
        const existTargetEvent = item.shipType !== 'NONE' && item.targetEvent.targetTempId > -1
        const retItem = {
          ...item,
          landingTipsDetail: (existTargetEvent && item.landingTipsDetail.length && item.landingTipsDetail[0] !== '')
            ? item.landingTipsDetail[0]
            : undefined,
          tipsTempId: item.tipsDetail === '' ? undefined : -1,
          seq: 0,
          additionalMediaSaveVOList: item.additionalMediaList.map(media => {
            return {
              ...media,
              tipsTempId: media.tipsDetail === '' ? undefined : -1,
              keyEventList: media.shipType === 'KEY' ? [media.targetEvent] : [genTargetEvent()],
              voiceEventList: media.shipType === 'VOICE' ? [media.targetEvent] : [genTargetEvent()],
              autoEventList: media.shipType === 'AUTO' ? [media.targetEvent] : [genTargetEvent()],
            }
          }),
        }
        retItem.keyEventList = item.shipType === 'KEY' ? [item.targetEvent] : [genTargetEvent()]
        retItem.voiceEventList = item.shipType === 'VOICE' ? [item.targetEvent] : [genTargetEvent()]
        retItem.autoEventList = item.shipType === 'AUTO' ? [item.targetEvent] : [genTargetEvent()]
        delete retItem.shipType
        delete retItem.targetEventType
        delete retItem.targetEvent

        if (item.codeType === 1) {
          shareTrackCode(retItem, this.shareTrackModel)
        }
        if (!existTargetEvent) {
          retItem.trackListLanding = []
          retItem.clickTrackListLanding = []
          retItem.startTrackListLanding = []
          retItem.middleTrackListLanding = []
          retItem.endTrackListLanding = []
        }
        retItem.feedContent = this.adSpaceModel.supportFeedContent === 1
          ? {
            creativeTitle: item.creativeTitle,
            clickButtonContent: (existTargetEvent && item.shipType === 'KEY') ? item.clickButtonContent : undefined
          }
          : undefined
        if (item.useMobileTrack === 0) {
          retItem.mobileTrackList = []
        }
        delete retItem.useMobileTrack

        return retItem
      })
      const params = {
        partnerId: this.scheduleForm.partnerId,
        scheduleId: this.scheduleForm.scheduleId,
        exposureMode: this.creativeModel.exposureMode,
        mediaAddUpdateVOList,
        settlementType: this.creativeModel.settlementType,
        settlementProvider: this.creativeModel.settlementType !== 0 ? this.creativeModel.settlementProvider : '',
        shareTrack: this.creativeModel.mediaModelList[0].codeType === 1 ? 1 : 0,
        backupMediaId: '',
        operateFlag: 2
      }
      return params
    },
    async validateCreative() {
      const isValid = await this.$refs.creativeForm.validate().catch(() => {})
      if (!isValid) return false
      const model = this.creativeModel
      for (let i = 0; i < model.mediaModelList.length; i++) {
        let media = model.mediaModelList[i]
        shareTrackCode(media, this.shareTrackModel)
        // 校验交互事件
        let targetEvent = media.targetEvent
        let existTargetEvent = media.shipType !== 'NONE' && targetEvent.targetTempId > -1
        let isVoiceEvent = media.shipType === 'VOICE' && targetEvent.targetTempId > -1
        // 语音触发校验唤醒词
        if (isVoiceEvent && !targetEvent.voiceWord) {
          this.$message.error(`素材${i + 1}语音触发的唤醒词不能为空!`)
          return false
        }
        // 校验按键、语音、自动事件的扩展参数
        if (existTargetEvent) {
          const eventErr = checkTargetEvent(targetEvent)
          if (eventErr) {
            this.$message.error(`素材${i + 1}${eventEnum[media.shipType].name}${eventErr}`)
            return false
          }
        }

        // 校验提示语
        if (this.scheduleForm.spaceNo === 'CCADTV10001' && !media.tipsDetail) {
          this.$message.error('开机广告提示语必填')
          return false
        }
        if (media.tipsDetail) {
          if (!checkTipsDetail(media.tipsDetail)) {
            this.$message.error(`素材${i + 1}提示语参数校验不完整, 请检查!`)
            return false
          }
        }
        // 校验监测代码
        let trackErrMsg = ''
        if (media.codeType !== 0) {
          let checkMedia = media.codeType === 1 ? this.shareTrackModel : media
          trackErrMsg = checkTrackCode(checkMedia, '', this.macroMap, this.providerMap, this.ownProvider)
          if (trackErrMsg) {
            this.$message.error(`素材${i + 1}${trackErrMsg}`)
            return false
          }
          debugger
          // 带附加素材的只能填曝光和点击
          if (media.additionalMediaList.length &&
            (media.startTrackList.length ||
            media.middleTrackList.length ||
            media.endTrackList.length ||
            media.pointTrackList.length)
          ) {
            this.$message.error(`素材${i + 1}包含附加素材，监测代码只能填写曝光和点击!`)
            return false
          }

          // 校验定点监测
          if (this.adSpaceModel.pointTrack === 1 && checkMedia.pointTrackList) {
            const ownTrackPoints = []
            const hasInvalidTime = checkMedia.pointTrackList.some((track) => {
              if (!track.point) {
                this.$message.error(`素材${i + 1}定点监测时间不能为空`)
                return true
              }
              if (parseInt(track.point) > media.duration) {
                this.$message.error(`素材${i + 1}定点监测时间不能>素材时长`)
                return true
              }
              if (this.ownProvider.provider === track.serviceProvider && track.point) {
                if (ownTrackPoints.includes(track.point)) {
                  this.$message.error(`素材${i + 1}定点监测${track.point}秒自有监测只能有一个!`)
                  return true
                }
                ownTrackPoints.push(track.point)
              }
              return false
            })
            if (hasInvalidTime) return false
          }
        }

        if (media.codeTypeLanding !== 0) {
          trackErrMsg = checkTrackCode(media, 'Landing', this.macroMap, this.providerMap, this.ownProvider)
          if (trackErrMsg) {
            this.$message.error(`素材${i + 1}落地页${trackErrMsg}`)
            return false
          }
        }

        // 校验feed流广告附加创意
        if (this.adSpaceModel.supportFeedContent === 1) {
          if (!media.creativeTitle) {
            this.$message.error(`素材${i + 1}创意标题不能为空`)
            return false
          }
          if (existTargetEvent && media.shipType === 'KEY' && !media.clickButtonContent) {
            this.$message.error(`素材${i + 1}点击按钮文案不能为空`)
            return false
          }
        }

        // 校验移动端监测代码
        if (this.scheduleForm.spaceNo === 'CCADTV10040' && media.useMobileTrack === 1) {
          let isComplete = true
          let isCorrect = true
          media.mobileTrackList.forEach(track => {
            if (!track.serviceProvider || !track.code) {
              isComplete = false
            }
            if (!checkRegEx(track.code)) {
              isCorrect = false
            }
          })
          if (!isComplete) {
            this.$message.error(`素材${i + 1}移动端监测代码填写不完整`)
            return false
          }
          if (!isCorrect) {
            this.$message.error(`素材${i + 1}移动端监测代码格式有误!`)
            return false
          }
        }
      }
      return true
    },
    handleGoBack() {
      this.$confirm('返回页面内容不会保存，确定要离开吗？', '提示', {
        confirmButtonText: '离开页面',
        cancelButtonText: '留在页面',
        center: true,
        type: 'warning'
      })
        .then(() => {
          this.$emit('go-back')
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="stylus" src="./materialOwnSource.styl" scoped></style>
